import React, { useMemo, useState } from "react";
import { apiService } from "../../../services/apiService";
import Header from "../../commonComponents/header/Header";
import arrowImg from "../../../assets/images/arrow-white-icon.svg";
import { Turnstile } from "@marsidev/react-turnstile";
const SCALE = [
  { value: 1, label: "Very Bad" },
  { value: 2, label: "Average" },
  { value: 3, label: "Good" },
  { value: 4, label: "Very Good" },
  { value: 5, label: "Excellent" },
];

const QUESTIONS = [
  "Overall Experience - How would you rate your overall experience using the Needles and Haystacks platform?",
  "Ease of Getting Started - How would you rate the ease of understanding the assessment process and instructions when you started?",
  "Navigation & Flow - How would you rate the ease of navigating through different stages of the assessment?",
  "User Interface (Design & Layout) - How would you rate the visual design, layout, and readability of the platform?",
  "User Experience During the Interview - How would you rate the smoothness and responsiveness of the platform while answering questions?",
  "System Usability & Difficulty - How would you rate the overall ease of using Needles and Haystacks without facing technical or usability issues?",
  "Clarity of Questions - How would you rate the clarity and understandability of the questions asked during the assessment?",
  "Relevance of Questions - How would you rate how relevant the assessment questions were to the role or skills being assessed?",
  "Comfort Level with AI Interviewing - How would you rate your comfort level being interviewed by an AI system?",
  "Overall Satisfaction - How would you rate your overall satisfaction with the Needles and Haystacks assessment experience?",
];

export default function SurveyForm() {
  const companyId = sessionStorage.getItem("companyId");

  const interviewId = sessionStorage.getItem("interviewId");
  // localStorage.getItem("interview_id");

  const [answers, setAnswers] = useState({});
  const [additionalComments, setAdditionalComments] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState(null);
  const [turnstileKey, setTurnstileKey] = useState(0);
  const [captchaMessage, setCaptchaMessage] = useState(
    "This platform uses a security service to protect against malicious bots. We are verifying you are not a bot.",
  );

  const allAnswered = useMemo(() => {
    return QUESTIONS.every((q) => answers[q] !== undefined);
  }, [answers]);

  const progress = useMemo(() => {
    const answered = QUESTIONS.filter((q) => answers[q] !== undefined).length;
    return Math.round((answered / QUESTIONS.length) * 100);
  }, [answers]);

  const onSelect = (question, value) => {
    setAnswers((prev) => ({ ...prev, [question]: value }));
  };

  const clearSurveySession = () => {
    // remove only the required keys
    sessionStorage.removeItem("interviewId");
    sessionStorage.removeItem("companyId");
    sessionStorage.removeItem("authToken");
    sessionStorage.removeItem("auth_token");
    sessionStorage.removeItem("access_token");

    localStorage.removeItem("interviewId");
    localStorage.removeItem("companyId");
    localStorage.removeItem("authToken");
    localStorage.removeItem("auth_token");
    localStorage.removeItem("access_token");
  };

  const handleSave = async () => {
    if (!companyId || !interviewId) {
      alert("Missing companyId/interviewId. Please contact support.");
      return;
    }

    if (!allAnswered) {
      alert("Please answer all questions.");
      return;
    }

    const payload = {
      interview_id: interviewId,
      company_id: companyId,
      survey_responses: [
        {
          ...Object.fromEntries(QUESTIONS.map((q) => [q, answers[q]])),
          "Is there anything specific you would like to tell us?":
            additionalComments,
        },
      ],
    };

    try {
      setLoading(true);
      const authToken =
        sessionStorage.getItem("auth_token") ||
        localStorage.getItem("auth_token") ||
        sessionStorage.getItem("access_token") ||
        localStorage.getItem("access_token") ||
        sessionStorage.getItem("authToken");

      const headers = {
        "Content-Type": "application/json",
      };

      // Add authorization header if token exists
      if (authToken) {
        headers["Authorization"] = `Bearer ${authToken}`;
      }

      const response = await apiService.setInterviewSurvey(payload);

      setSubmitted(true);

      clearSurveySession();
    } catch (e) {
      console.error(e);
      alert("Failed to submit survey. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return submitted ? (
    <div className="min-h-screen relative overflow-hidden flex items-center justify-center px-4 py-12 bg-gradient-to-br from-purple-50 via-white to-gray-50">
      {/* soft background blobs */}
      <div className="absolute -top-24 -left-24 h-72 w-72 rounded-full bg-purple-200/40 blur-3xl" />
      <div className="absolute -bottom-24 -right-24 h-72 w-72 rounded-full bg-blue-200/40 blur-3xl" />

      <div
        className="relative w-full max-w-xl rounded-3xl border border-white/60 bg-white/80 backdrop-blur-xl shadow-[0_18px_50px_rgba(17,23,41,0.12)] p-10 text-center
               animate-[fadeIn_0.4s_ease-out]"
        style={{ color: "#111729" }}
      >
        {/* icon */}
        <div className="mx-auto mb-6 w-20 h-20 rounded-full bg-[#111729] flex items-center justify-center ring-8 ring-[#111729]/20 ">
          <svg
            className="w-10 h-10 text-white"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M5 13l4 4L19 7"
            />
          </svg>
        </div>

        <h2 className="app-title font-semibold tracking-tight mb-2">
          Thank you!
        </h2>

        <p className="app-body leading-relaxed text-gray-600 mb-8">
          Your survey was submitted successfully.
          <br />
          You may now safely close this window.
        </p>
      </div>

      {/* small keyframes */}
      <style>
        {`
      @keyframes fadeIn {
        from { opacity: 0; transform: translateY(10px) scale(0.98); }
        to   { opacity: 1; transform: translateY(0) scale(1); }
      }
    `}
      </style>
    </div>
  ) : (
    <>
      {" "}
      <div className="mt-4 ml-4">
        <Header />
      </div>
      <div
        className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-gray-50 py-12 px-4"
        style={{ color: "#111729" }}
      >
        <div className="w-full max-w-6xl mx-auto">
          {/* Header */}

          {/* Questions */}
          <div className="space-y-6">
            {QUESTIONS.map((q, index) => {
              const [title, description] = q.split(" - ");
              return (
                <div
                  key={q}
                  className="bg-white rounded-2xl shadow-md hover:shadow-lg transition-shadow duration-300 p-6 border-2 border-transparent "
                >
                  <div className="flex items-start gap-4 mb-5">
                    <div className="flex-1">
                      <p className="text-[22px] font-medium">{description}</p>
                    </div>
                    {answers[q] && (
                      <div className="flex-shrink-0">
                        <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-purple-100 to-blue-100">
                          <svg
                            className="w-4 h-4 text-purple-600"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                              clipRule="evenodd"
                            />
                          </svg>
                          <span className="text-sm font-semibold text-purple-700">
                            {SCALE.find((s) => s.value === answers[q])?.label}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="grid grid-cols-5 gap-3">
                    {SCALE.map((s) => {
                      const active = answers[q] === s.value;

                      return (
                        <button
                          type="button"
                          key={s.value}
                          onClick={() => onSelect(q, s.value)}
                          onMouseEnter={(e) => {
                            if (!active)
                              e.currentTarget.style.backgroundColor = "#eaeaea";
                          }}
                          onMouseLeave={(e) => {
                            if (!active)
                              e.currentTarget.style.backgroundColor = "white";
                          }}
                          className={`group relative rounded-xl border-2 px-3 py-4 transition-all duration-200 transform hover:scale-105
    ${active ? "shadow-lg" : "bg-white hover:shadow-md"}`}
                          style={
                            active
                              ? {
                                  borderColor: "#111729",
                                  backgroundColor: "#eaeaea",
                                  color: "#111729",
                                  fontWeight: "normal",
                                  fontSize: "18px",
                                }
                              : {
                                  borderColor: "#d1d5db",
                                  backgroundColor: "white",
                                  color: "#111729",
                                  fontWeight: "normal",
                                  fontSize: "18px",
                                }
                          }
                        >
                          <div className="mb-1">{s.value}</div>
                          <div>{s.label}</div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
            <div className="bg-white rounded-2xl shadow-md hover:shadow-lg transition-shadow duration-300 p-6 border-2 border-transparent">
              <div className="flex items-start gap-4 mb-5">
                <div className="flex-1">
                  <p className="text-[22px] font-medium">
                    Is there anything specific you would like to tell us?
                  </p>
                </div>
                {additionalComments.trim().length > 0 && (
                  <div className="flex-shrink-0">
                    <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-purple-100 to-blue-100">
                      <svg
                        className="w-4 h-4 text-purple-600"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                      <span className="text-sm font-semibold text-purple-700">
                        Added
                      </span>
                    </div>
                  </div>
                )}
              </div>
              <textarea
                rows={5}
                value={additionalComments}
                onChange={(e) => setAdditionalComments(e.target.value)}
                placeholder="Type your response here..."
                className="w-full rounded-xl border-2 border-gray-200 focus:border-[#111729] focus:outline-none px-4 py-3 text-[16px] text-[#111729] resize-none transition-colors duration-200 placeholder-gray-400"
              />
            </div>
          </div>

          {/* Submit Button */}
          <div className="mt-10 bg-white rounded-2xl shadow-lg p-6">
            {/* Cloudflare Turnstile — aligned with questions */}
            <div className="mb-6">
              <p className="app-body font-medium mb-2">{captchaMessage}</p>

              <Turnstile
                key={turnstileKey}
                siteKey={process.env.REACT_APP_TURNSTILE_SITE_KEY}
                onSuccess={(token) => {
                  setTurnstileToken(token);
                  setCaptchaMessage(
                    "Verification successful. Please click on Submit Survey.",
                  );
                }}
                onExpire={() => {
                  setTurnstileToken(null);
                  setCaptchaMessage(
                    "This platform uses a security service to protect against malicious bots. We are verifying you are not a bot.",
                  );
                }}
                onError={() => {
                  setTurnstileToken(null);
                  setTurnstileKey((k) => k + 1);
                  setCaptchaMessage(
                    "This platform uses a security service to protect against malicious bots. We are verifying you are not a bot.",
                  );
                }}
                options={{
                  theme: "light",
                  size: "normal",
                  appearance: "always",
                }}
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <p className="text-[18px]">
                  {allAnswered
                    ? "All questions answered. Click submit when ready."
                    : ` ${
                        QUESTIONS.length -
                        QUESTIONS.filter((q) => answers[q]).length
                      } Remaining`}
                </p>
              </div>
              <button
                type="button"
                disabled={!allAnswered || loading || !turnstileToken}
                onClick={handleSave}
                className="flex items-center justify-center px-6 py-1.5 btn-size bg-btnBgColor text-white text-[16px] disabled:opacity-30 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                        fill="none"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    Submitting...
                  </span>
                ) : (
                  <>
                    Submit Survey
                    <img src={arrowImg} alt="arrow" className="ml-2" />
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
